// RequireRole — route-level role guard.
//
// Runs INSIDE Authed (which already proves the user is signed in and
// has an employee role mapping). RequireRole adds a per-page check on
// top of that, redirecting unauthorized users to /dashboard with a
// `notice` query param the toast system reads on the next page so we
// can surface a one-line "you don't have access" message without
// inventing a global toast bus mid-flight.
//
// `predicate` takes the SessionUser and returns true when the user is
// allowed. Returning false triggers the redirect; the original
// destination isn't preserved (unlike RequireAuth) because the user
// can't access it anyway.
//
// Server-side enforcement is the real boundary; this guard is UX. Any
// admin endpoint must independently reject non-admin requests.

import { Navigate } from "react-router-dom";

import type { SessionUser } from "../api";
import { useSession } from "../session";

interface RequireRoleProps {
  predicate: (user: SessionUser) => boolean;
  /** Page-name used in the redirect notice (e.g. "Automations"). */
  pageName: string;
  children: React.ReactNode;
}

export function RequireRole({
  predicate,
  pageName,
  children,
}: RequireRoleProps) {
  const { user } = useSession();
  if (!user) {
    // Defensive — RequireAuth wraps RequireRole, so this branch
    // shouldn't fire in practice. Send the user to /sign-in via
    // /dashboard's own guard chain.
    return <Navigate to="/sign-in" replace />;
  }
  if (!predicate(user)) {
    const notice = `You don't have access to ${pageName}.`;
    return (
      <Navigate
        to={`/dashboard?notice=${encodeURIComponent(notice)}`}
        replace
      />
    );
  }
  return <>{children}</>;
}
